import { Divider, Illustration, Text, Title } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Stack } from "@mui/material";
import { useGetAllowedWindowToScheduleOrientation } from "@src/appV2/ClipboardOrientation/hooks/useGetAllowedWindowToScheduleOrientation";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { DateTimePicker } from "@src/appV2/lib/DatePicker";
import { LoadingButton } from "@src/appV2/lib/LoadingButton/LoadingButton";
import { PickerButton } from "@src/appV2/redesign/components/PickerButton";
import { useScheduleAssessment } from "@src/appV2/SkillsAssessment/api/useScheduleAssessment";
import { useSkillsAssessmentFeatureFlags } from "@src/appV2/SkillsAssessment/api/useSkillsAssessmentFeatureFlags";
import {
  type SkillsAssessment,
  type SkillsAssessmentStatus,
} from "@src/appV2/SkillsAssessment/types";
import { adjustMinimumDatePickerTime } from "@src/appV2/SkillsAssessment/utils/adjustMinimumDatePickerTime";
import { checkIsWorkerAllowedToRescheduleAssessment } from "@src/appV2/SkillsAssessment/utils/checkIsWorkerAllowedToRescheduleAssessment";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { differenceInHours, isValid, parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { format } from "date-fns-tz";
import pluralize from "pluralize";
import { useState } from "react";

interface Props {
  isRefetchingData: boolean;
  assessment: SkillsAssessment;
  scheduledFor: string;
  showTimezone: boolean;
  onReschedule: () => void;
  assessmentStatus: SkillsAssessmentStatus;
}

export function ClipboardOrientationScheduled(props: Props) {
  const {
    isRefetchingData,
    scheduledFor,
    showTimezone,
    onReschedule,
    assessmentStatus,
    assessment,
  } = props;

  const worker = useDefinedWorker();

  const { scheduledAssessmentConfig } = useSkillsAssessmentFeatureFlags();
  const {
    minimumLeadTimeToScheduleAssessmentInHours,
    maximumLeadTimeToScheduleAssessmentInHours,
    rescheduleLeadTimeInMinutes,
    allowedTimeRange,
  } = scheduledAssessmentConfig;

  const isAllowedToReschedule = checkIsWorkerAllowedToRescheduleAssessment({
    assessmentType: assessmentStatus.attributes.type,
    rescheduleLeadTimeInMinutes,
    assessmentScheduledFor: scheduledFor,
    assessmentStatus: assessmentStatus.attributes.status,
  });

  const [rescheduledFor, setRescheduledFor] = useState<string | undefined>();

  const { minDate, maxDate } = useGetAllowedWindowToScheduleOrientation();

  const {
    mutateAsync: scheduleAssessment,
    isLoading,
    isError,
  } = useScheduleAssessment({
    useErrorBoundary: (error) => error.response?.status !== 400,
  });

  const rescheduleLeadTimeInHours = Math.floor(rescheduleLeadTimeInMinutes / 60);

  const dateTimePickerFormat = "MMM d, h:mm a";
  let dateTimePickerText =
    isDefined(rescheduledFor) && isValid(new Date(rescheduledFor))
      ? format(new Date(rescheduledFor), dateTimePickerFormat)
      : `Pick a different time if needed`;

  // When the user is not allowed to reschedule, we show the original scheduled time
  if (!isAllowedToReschedule && isDefined(scheduledFor)) {
    dateTimePickerText = format(parseISO(scheduledFor), dateTimePickerFormat);
  }

  return (
    <Stack justifyContent="space-between" width="100%" height="100%">
      <Stack spacing={8}>
        <Title variant="h3" component="h2">
          You booked your Demo Shift for{" "}
          <span>
            {format(parseISO(scheduledFor), "MMMM do")} at{" "}
            {format(parseISO(scheduledFor), `h:mmaaa${showTimezone ? " z" : ""}`)}
          </span>
        </Title>

        <Stack py={6} alignItems="center">
          <Illustration type="shift-booked" />
        </Stack>

        <Text light>
          Come back to the app at your scheduled time to show your punctuality for shifts. We also
          sent you a text with the link to join the Demo Shift.
        </Text>

        <Divider />

        <Text bold>
          Remember: think of it like your first shift at a facility on Clipboard. We&apos;re
          counting on you to show up on time!
        </Text>

        <Stack spacing={2}>
          <DateTimePicker
            minDateTime={adjustMinimumDatePickerTime(minDate)}
            maxDateTime={maxDate}
            disabled={isLoading || isRefetchingData || !isAllowedToReschedule}
            allowedTimeRange={allowedTimeRange}
            triggerButton={(props) => {
              return (
                <PickerButton
                  primaryText={dateTimePickerText}
                  sx={(theme) => ({
                    // Don't show the dark background if the user has input a new time
                    ...(!isDefined(rescheduledFor) && {
                      backgroundColor: theme.palette.border?.darkInvert,
                    }),
                    border: `1px solid ${theme.palette.border?.main}`,
                    "&:focus": {
                      border: `1px solid ${theme.palette.border?.main}`,
                    },
                    "&:hover": {
                      border: `1px solid ${theme.palette.border?.main}`,
                    },
                  })}
                  startIconType="clock-filled"
                  // Adding empty secondaryText to override default margin-left: auto on button's last child
                  secondaryText={" "}
                  isSelected={isDefined(rescheduledFor)}
                  {...props}
                />
              );
            }}
            onConfirm={(value) => {
              setRescheduledFor(value.toISOString());
            }}
          />

          <Text light variant="body2" align="center">
            You can only reschedule up to {rescheduleLeadTimeInHours}{" "}
            {pluralize("hour", rescheduleLeadTimeInHours)} before start
          </Text>

          {isError && (
            <Alert severity="error">
              The selected time must be at least {minimumLeadTimeToScheduleAssessmentInHours} hours
              in the future and at most{" "}
              {Math.round(maximumLeadTimeToScheduleAssessmentInHours / 24)} days in the future.
            </Alert>
          )}
        </Stack>
      </Stack>

      {isAllowedToReschedule && (
        <LoadingButton
          variant="contained"
          isLoading={isLoading || isRefetchingData}
          disabled={
            isLoading ||
            isRefetchingData ||
            !isDefined(rescheduledFor) ||
            scheduledFor === rescheduledFor
          }
          onClick={async () => {
            await scheduleAssessment({
              assessmentId: assessment.id,
              workerId: worker.userId,
              scheduledFor: rescheduledFor!,
            });
            logEvent(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_RESCHEDULED, {
              assessmentId: assessment.id,
              workerId: worker.userId,
              originalScheduledFor: scheduledFor,
              newScheduledFor: rescheduledFor!,
              hoursUntilOriginalScheduledFor: differenceInHours(parseISO(scheduledFor), new Date()),
              hoursUntilNewScheduledFor: differenceInHours(parseISO(rescheduledFor!), new Date()),
            });

            onReschedule();
          }}
        >
          Confirm
        </LoadingButton>
      )}
    </Stack>
  );
}
